import type {
  EmulatorErrorContext,
  EmulatorLaunchRequest,
  EmulatorOperation,
  EmulatorRuntimeErrorCode,
  EmulatorRuntimeIdentity,
  EmulatorRuntimeState,
} from './contracts';
import type { EmulatorRuntimeManifest } from './runtime-manifest';

const sha256Pattern = /^[a-f0-9]{64}$/;

const allowedTransitions: Readonly<Record<EmulatorRuntimeState, readonly EmulatorRuntimeState[]>> =
  {
    idle: ['loading', 'terminated'],
    loading: ['ready', 'error', 'terminated'],
    ready: ['paused', 'error', 'terminated'],
    paused: ['ready', 'error', 'terminated'],
    error: ['loading', 'terminated'],
    terminated: ['loading'],
  };

const allowedOperations: Readonly<Record<EmulatorOperation, readonly EmulatorRuntimeState[]>> = {
  launch: ['idle', 'error', 'terminated'],
  pause: ['ready'],
  resume: ['paused'],
  restart: ['ready', 'paused'],
  'set-volume': ['loading', 'ready', 'paused'],
  fullscreen: ['ready', 'paused'],
  'export-native-save': ['ready', 'paused'],
  'import-native-save': ['ready', 'paused'],
  'capture-save-state': ['ready', 'paused'],
  'restore-save-state': ['ready', 'paused'],
  'set-cheat': ['ready', 'paused'],
  'reset-cheats': ['ready', 'paused'],
  terminate: ['loading', 'ready', 'paused', 'error'],
};

export class EmulatorContractError extends Error {
  readonly code: EmulatorRuntimeErrorCode;
  readonly recoverable: boolean;
  readonly context: EmulatorErrorContext | undefined;

  constructor(
    code: EmulatorRuntimeErrorCode,
    message: string,
    recoverable: boolean,
    context?: EmulatorErrorContext,
  ) {
    super(message);
    this.name = 'EmulatorContractError';
    this.code = code;
    this.recoverable = recoverable;
    this.context = context;
  }
}

export function assertRuntimeTransition(
  from: EmulatorRuntimeState,
  to: EmulatorRuntimeState,
): void {
  if (!allowedTransitions[from].includes(to)) {
    throw new EmulatorContractError(
      'INVALID_STATE_TRANSITION',
      `Runtime transition ${from} -> ${to} is not allowed.`,
      false,
      { state: from },
    );
  }
}

export function assertOperationAllowed(
  operation: EmulatorOperation,
  state: EmulatorRuntimeState,
): void {
  if (!allowedOperations[operation].includes(state)) {
    throw new EmulatorContractError(
      'OPERATION_NOT_ALLOWED',
      `Operation ${operation} is not allowed while runtime state is ${state}.`,
      true,
      { operation, state },
    );
  }
}

export function validateLaunchRequest(request: EmulatorLaunchRequest): void {
  if (!request.gameId.trim() || !request.variantId.trim() || !request.displayName.trim()) {
    throw new EmulatorContractError(
      'INVALID_LAUNCH_REQUEST',
      'Launch request requires gameId, variantId, and displayName.',
      true,
      { operation: 'launch' },
    );
  }

  if (request.platform !== 'GBA') {
    throw new EmulatorContractError(
      'INVALID_LAUNCH_REQUEST',
      `Unsupported launch platform: ${request.platform}.`,
      true,
      { operation: 'launch', gameId: request.gameId, variantId: request.variantId },
    );
  }

  if (!sha256Pattern.test(request.romSha256)) {
    throw new EmulatorContractError(
      'INVALID_LAUNCH_REQUEST',
      'ROM SHA-256 must be a lowercase 64-character hexadecimal digest.',
      true,
      { operation: 'launch', gameId: request.gameId, variantId: request.variantId },
    );
  }

  let protocol: string;
  try {
    protocol = new URL(request.romUrl).protocol;
  } catch {
    throw new EmulatorContractError(
      'INVALID_LAUNCH_REQUEST',
      'ROM URL must be a valid absolute URL.',
      true,
      { operation: 'launch', gameId: request.gameId, variantId: request.variantId },
    );
  }

  if (protocol !== 'https:' && protocol !== 'blob:') {
    throw new EmulatorContractError(
      'INVALID_LAUNCH_REQUEST',
      'ROM URL must use HTTPS or a browser blob URL.',
      true,
      { operation: 'launch', gameId: request.gameId, variantId: request.variantId },
    );
  }
}

export function createRuntimeIdentity(
  runtime: EmulatorRuntimeManifest,
  request: EmulatorLaunchRequest,
): EmulatorRuntimeIdentity {
  validateLaunchRequest(request);

  return {
    runtimeId: runtime.runtimeId,
    gameId: request.gameId,
    variantId: request.variantId,
    romSha256: request.romSha256,
    gameKey: `${request.variantId}:${request.romSha256}`,
  };
}
